import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectors as authSelectors } from 'auth';
import { selectors as sessionSelectors } from 'session';
import { api } from 'app/services';
import {
  safeStorageGet,
  safeStorageSet,
  safeStorageRemove,
} from 'common/storageUtils';
import actions from 'session/actions';

const SNOOZE_DAYS = 3;

const getSnoozeKey = (organizationId) =>
  `regulatoryConsent:snooze:${organizationId}`;

const getSnoozeExpiry = () => {
  const date = new Date();
  date.setDate(date.getDate() + SNOOZE_DAYS);
  return date.toISOString();
};

const isSnoozeExpired = (timestamp) => {
  if (!timestamp) return true;
  return new Date(timestamp) <= new Date();
};

const useRegulatoryConsent = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const activeOrganization = useSelector(sessionSelectors.activeOrganization);

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const organizationId = activeOrganization?.id;
  const shouldShowConsent = activeOrganization?.showCifraConsentPopup;

  const snoozeKey = organizationId ? getSnoozeKey(organizationId) : null;

  useEffect(() => {
    if (!isAuthenticated || !organizationId || !shouldShowConsent) {
      setIsOpen(false);
      return;
    }

    const snoozeExpiry = safeStorageGet(snoozeKey);

    if (!snoozeExpiry || isSnoozeExpired(snoozeExpiry)) {
      safeStorageRemove(snoozeKey);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [isAuthenticated, organizationId, shouldShowConsent, snoozeKey]);

  const accept = async () => {
    if (!organizationId) return;

    setIsSubmitting(true);
    try {
      const { error } = await api.submitCifraConsent(organizationId);
      if (error) throw error;

      safeStorageRemove(snoozeKey);
      dispatch(actions.organizationShowCifraConsent(false));
      setIsOpen(false);
    } catch (error) {
      // On API error, snooze the modal so user can access the dashboard
      // and will be reminded again in 3 days
      safeStorageSet(snoozeKey, getSnoozeExpiry());
      setIsOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const remindLater = () => {
    safeStorageSet(snoozeKey, getSnoozeExpiry());
    setIsOpen(false);
  };

  return { isOpen, isSubmitting, accept, remindLater };
};

export default useRegulatoryConsent;
